import { FC, lazy, Suspense } from "react";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { App } from "../App";
import { MasterLayout } from "../layouts/MasterLayout";
import { LanderV2Layout } from "../layouts/LanderV2Layout";
import { AuthPage } from "../modules/auth";
import { Registration } from "../modules/auth/components/Registration";
import { UserAuthContextProvider, useUserAuth } from "../context/AuthContext";
import { PortalContextProvider } from "../context/PortalContext";
import ProtectedRoute from "./ProtectedRoute";
import RequireAuth from "./RequireAuth";
import { AuthLayout } from "../layouts/AuthLayout";
import LanderV2 from "../landers/LanderV2";
import { LoggerContextProvider } from "../context/LoggerContext";
import ConditionalRoute from "./ConditionalRoute";
import { JourneyContextProvider } from "../context/JourneyContext";
import ScrollToTop from "../modules/common/other/ScrollToTop";
import { RouteFallback } from "./RouteFallback";
import { GlobalLoaderContextProvider } from "../context/GlobalLoaderContext";

// Route-level code splitting: everything below is only needed for its own
// journey, not for the lander page, so it's excluded from the initial bundle.
const ErrorsPage = lazy(() =>
  import("../modules/errors/ErrorsPage").then((m) => ({ default: m.ErrorsPage }))
);
const Home = lazy(() => import("../pages/Home").then((m) => ({ default: m.Home })));
const QuickQuote = lazy(() =>
  import("../pages/annuity-quote/quick_quote/QuickQuote").then((m) => ({
    default: m.QuickQuote,
  }))
);
const GetStarted = lazy(() =>
  import("../pages/GetStarted").then((m) => ({ default: m.GetStarted }))
);
const Factfind = lazy(() =>
  import("../pages/fact-find/LoggedInFactFind/FactFind").then((m) => ({
    default: m.Factfind,
  }))
);
const BookingFull = lazy(() =>
  import("../pages/booking/BookingFull").then((m) => ({ default: m.BookingFull }))
);
const FactfindCompleted = lazy(() =>
  import("../pages/fact-find/LoggedInFactFind/FactfindCompleted").then((m) => ({
    default: m.FactfindCompleted,
  }))
);
const Lander = lazy(() => import("../landers/Lander"));
const PilotCard = lazy(() =>
  import("../pages/PilotCard").then((m) => ({ default: m.PilotCard }))
);
const LoggedOutPensionReview = lazy(
  () => import("../pages/pension-review/PensionReviewLoggedOut/LoggedOutPensionReview")
);
const PensionReview = lazy(() =>
  import("../pages/pension-review/PensionReviewLoggedIn/PensionReview").then((m) => ({
    default: m.PensionReview,
  }))
);
const PensionReviewSubmission = lazy(() =>
  import("../pages/pension-review/PensionReviewLoggedIn/PensionReviewSubmission").then(
    (m) => ({ default: m.PensionReviewSubmission })
  )
);
const AnnuityQuotesQuick = lazy(() =>
  import("../pages/annuity-quote/AnnuityQuotesQuick").then((m) => ({
    default: m.AnnuityQuotesQuick,
  }))
);
const FullQuote = lazy(() => import("../pages/annuity-quote/full_quote/FullQuote"));
const AnnuityQuotes = lazy(() => import("../pages/annuity-quote/AnnuityQuotes"));
const AnnuityQuotesOld = lazy(() =>
  import("../pages/annuity-quote/AnnuityQuotesOld").then((m) => ({
    default: m.AnnuityQuotesOld,
  }))
);
const AnnuityQuickQuotesLoggedOut = lazy(
  () => import("../pages/annuity-quote/AnnuityQuickQuotesLoggedOut")
);
const LoggedOutFactFind = lazy(
  () => import("../pages/fact-find/LoggedOutFactFind/LoggedOutFactFind")
);
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));
const CostOfDelay = lazy(() => import("../pages/cost-of-delay/CostOfDelay"));
const MyDetails = lazy(() =>
  import("../pages/my-details/MyDetails").then((m) => ({ default: m.MyDetails }))
);
const AnnualReviewBooking = lazy(() =>
  import("../pages/booking/AnnualReviewBooking").then((m) => ({
    default: m.AnnualReviewBooking,
  }))
);
const AdhocAppointmentBooking = lazy(() =>
  import("../pages/booking/AdhocAppointmentBooking").then((m) => ({
    default: m.AdhocAppointmentBooking,
  }))
);

const { PUBLIC_URL } = process.env;

const resolveRedirect = (search: string): string => {
  const params = new URLSearchParams(search);
  const raw = (params.get("redirect") || "").trim();
  if (!raw) return "/";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("//")) return "/";
  const candidate = raw.startsWith("/") ? raw : `/${raw}`;
  if (!/^[/A-Za-z0-9\-._~%/?#=&]*$/.test(candidate)) return "/";
  return candidate;
};

const AuthRouteRedirect: FC = () => {
  const { user, loading } = useUserAuth();
  const redirectTo = resolveRedirect(window.location.search);

  if (loading) return null;

  return user ? <Navigate to={redirectTo} replace /> : <AuthPage />;
};

const RegisterRouteRedirect: FC = () => {
  const { user, loading } = useUserAuth();
  const redirectTo = resolveRedirect(window.location.search);

  if (loading) return null;

  return user ? <Navigate to={redirectTo} replace /> : <AuthLayout />;
};

const RegisterIndexRedirect: FC = () => {
  const { user, loading } = useUserAuth();
  const redirectTo = resolveRedirect(window.location.search);

  if (loading) return null;

  return user ? <Navigate to={redirectTo} replace /> : <Registration />;
};

const AppRoutes: FC = () => {
  const loggedInRoutes = (
    <>
      <Route path="/overview" element={<Dashboard />} />

      <Route
        path="/fullquotepartial"
        element={<FullQuote partialSchema={true} />}
      />
      <Route path="/fullquote" element={<FullQuote />} />
      <Route
        path="/annuityquotes"
        element={<AnnuityQuotes refreshOnLoad={false} quickQuote={false} />}
      />
      <Route
        path="/annuityquotesold000112"
        element={<AnnuityQuotesOld refreshOnLoad={false} quickQuote={false} />}
      />
      <Route
        path="/costofdelay"
        element={<CostOfDelay />}
      />
      <Route
        path="/annuityquotesquick"
        element={<AnnuityQuotesQuick refreshOnLoad={true} quickQuote={true} />}
      />
      <Route
        path="/annuityquotes/refresh"
        element={<AnnuityQuotes refreshOnLoad={true} quickQuote={false} />}
      />

      <Route path="/Pilot" element={<PilotCard />} />
      <Route
        path="/factfindcompleted"
        element={
          <ProtectedRoute
          // requireFullQuote requireFactfindCompleted
          >
            <FactfindCompleted />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pensionreview/complete"
        element={<PensionReviewSubmission />}
      ></Route>
      <Route
        path="/bookifacall"
        element={
          <ProtectedRoute
          // requireFullQuote requireFactfindCompleted
          >
            <BookingFull/>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-details"
        element={
          <ProtectedRoute
          // requireFullQuote requireFactfindCompleted
          >
            <MyDetails/>
          </ProtectedRoute>
        }
      />
      <Route
        path="/annual-review-booking"
        element={
          <ProtectedRoute
          // requireFullQuote requireFactfindCompleted
          >
            <AnnualReviewBooking/>
          </ProtectedRoute>
        }
      />
    </>
  );

  const conditionalRoutes = (
    <>
      <Route
        path="/factfind"
        element={
          <ConditionalRoute
            authenticatedComponent={
              <ProtectedRoute
              >
                <Factfind />
              </ProtectedRoute>
            }
            unauthenticatedComponent={<LoggedOutFactFind />}
          />
        }
      ></Route>

      <Route
        path="/*"
        element={
          <ConditionalRoute
            authenticatedComponent={<Navigate to="/overview" replace />}
            unauthenticatedComponent={<Home />}
          />
        }
      ></Route>
      <Route
        path="/pensionreview"
        element={<ConditionalRoute
          authenticatedComponent={<PensionReview />}
          unauthenticatedComponent={<LoggedOutPensionReview />}
        />}
      ></Route>
    </>
  );

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <ScrollToTop />
      <LoggerContextProvider>
        <UserAuthContextProvider>
          <PortalContextProvider>
            <JourneyContextProvider>
              <GlobalLoaderContextProvider>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route element={<App />}>
                  <Route element={<LanderV2Layout />}>
                    <Route
                      path="/landers/v2/:campaignSlug/*"
                      element={<LanderV2 />}
                    ></Route>
                    <Route
                      path="/book-adhoc-appointment"
                      element={<AdhocAppointmentBooking />}
                    ></Route>
                  </Route>
                  <Route element={<MasterLayout />}>
                    <Route element={<RequireAuth />}>{loggedInRoutes}</Route>
                    {conditionalRoutes}
                    {/*If you are logged in the '/' will take you to the dashboard otherwise it will take you to the homepage*/}
                    <Route path="/home" element={<Home />}></Route>
                    <Route path="/quickquote2" element={<QuickQuote />}></Route>
                    <Route path="/quickquote" element={<QuickQuote />}></Route>
                    <Route path="/getstarted" element={<GetStarted />}></Route>
                    {/*If you are not logged in then landers are accessible. If you are logged in then renders FullQuote*/}
                    <Route
                      path="/landers/:campaign_lander"
                      element={<Lander />}
                    ></Route>
                    <Route
                      path="/landers/:campaign_lander/bing"
                      element={<Lander />}
                    ></Route>
                    <Route
                      path="/landers/:campaign_lander/google"
                      element={<Lander />}
                    ></Route>
                    <Route
                      path="/landers/:campaign_lander/meta"
                      element={<Lander />}
                    ></Route>
                    <Route
                      path="/quickquote/annuityquotes"
                      element={
                        <AnnuityQuotes refreshOnLoad={true} quickQuote={true} />
                      }
                    ></Route>
                    <Route
                      path="/annuityquotesquickloggedout"
                      element={
                        <ConditionalRoute
                          authenticatedComponent={<Navigate to="/overview" replace />}
                          unauthenticatedComponent={<AnnuityQuickQuotesLoggedOut />}
                        />
                      }
                    ></Route>
                    <Route
                      path="auth/*"
                      element={<AuthRouteRedirect />}
                    />

                    <Route
                      path="register/*"
                      element={<RegisterRouteRedirect />}
                    >
                      <Route
                        index
                        element={<RegisterIndexRedirect />}
                      />
                    </Route>

                    <Route path="error/*" element={<ErrorsPage />} />
                  </Route>
                </Route>
              </Routes>
              </Suspense>
              </GlobalLoaderContextProvider>
            </JourneyContextProvider>
          </PortalContextProvider>
        </UserAuthContextProvider>
      </LoggerContextProvider>
    </BrowserRouter>
  );
};

export { AppRoutes };
